/** biome-ignore-all lint/a11y/noStaticElementInteractions: Event delegation to handle link clicks */
/** biome-ignore-all lint/a11y/useKeyWithClickEvents: Event delegation to handle link clicks */
import type { Dispatch, SetStateAction } from "react";
import ToolCard from "@/app/(main)/calculations/_components/tool-card";
import AdvisorCard from "@/app/(main)/committee/_components/advisor-card";
import type { Item as AdvisorItem } from "@/app/(main)/committee/_components/types";
import type { Topic } from "@/app/(main)/forum/_components/types";
import type { Ebook } from "@/types/academy";
import type { Author } from "@/types/content";
import type { ContentCard } from "@/types/dashboard";
import SearchAcademyCard, {
  type AcademySearchResult,
} from "./_components/search-academy-card";
import SearchConsultingCard, {
  type ConsultingSearchResult,
} from "./_components/search-consulting-card";
import SearchEbookCard from "./_components/search-ebook-card";
import SearchHorizontalContentCard from "./_components/search-horizontal-content-card";
import SearchNameCard, {
  type BabyNameSearchResult,
} from "./_components/search-name-card";
import SearchTopicCard from "./_components/search-topic-card";

const SECTION_TYPE_ACADEMY = 5;
const SECTION_TYPE_BABY_NAMES = 7;
const SECTION_TYPE_TOOLS = 8;
const SECTION_TYPE_EBOOKS = 9;
const SECTION_TYPE_CONSULTING = 10;
const SECTION_TYPE_ADVISORS = 11;

export interface ToolSearchResult {
  id: string;
  displayOrder?: number;
  toolType?: number;
  name: string;
  slug: string;
  description: string;
  imageUrl: string;
  platformType?: number;
}

export interface SearchResultContent {
  id: string;
  type: number;
  title: string;
  slug: string;
  lead?: string;
  summary?: string;
  thumbnailUrl?: string;
  coverImageUrl?: string;
  publishedAt?: string;
  publishedLabel?: string;
  likeCount?: number;
  commentCount?: number;
  viewCount?: number;
  isLiked?: boolean;
  isSaved?: boolean;
  author?: Author;
  categoryNames?: string[];
  categories?: {
    id: string;
    name: string;
    slug: string;
  }[];
}

export type SearchResultItem =
  | SearchResultContent
  | Topic
  | AcademySearchResult
  | ToolSearchResult
  | Ebook
  | ConsultingSearchResult
  | AdvisorItem
  | BabyNameSearchResult;

export type SearchResult = SearchResultItem;

interface SearchItemsProps {
  results: SearchResultItem[];
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  activeSectionId: string;
}

const mapToContentCard = (result: SearchResultContent): ContentCard => {
  return {
    author: result.author || {
      avatar: "",
      bio: "",
      id: "",
      isFollowing: false,
      isVerified: false,
      name: "",
      socialMediaLinks: [],
      title: "",
    },
    category: result.categories?.[0]
      ? {
          id: result.categories[0].id,
          name: result.categories[0].name,
          parent: null,
          slug: result.categories[0].slug,
        }
      : {
          id: "",
          name: result.categoryNames?.[0] || "",
          parent: null,
          slug: "",
        },
    commentCount: result.commentCount || 0,
    coverImageUrl: result.coverImageUrl,
    durationLabel: "",
    id: result.id,
    isBookmarked: result.isSaved || false,
    isCommented: false,
    isLiked: result.isLiked || false,
    lead: result.lead || result.summary || "",
    likeCount: result.likeCount || 0,
    publishedAt: result.publishedAt || "",
    publishedLabel: result.publishedLabel || "",
    slug: result.slug,
    summary: result.summary,
    thumbnailUrl: result.thumbnailUrl,
    title: result.title,
    type: result.type,
    viewCount: result.viewCount || 0,
  };
};

export function SearchItems({
  results,
  setIsOpen,
  activeSectionId,
}: SearchItemsProps) {
  if (results.length === 0) {
    return (
      <div className="text-center py-12 text-neutral-500">
        Sonuç bulunamadı.
      </div>
    );
  }

  if (activeSectionId === "8") {
    return (
      <ul
        className="grid grid-cols-1 md:grid-cols-2 gap-4 py-6 list-none"
        onClick={(e) => {
          const target = e.target as HTMLElement;
          if (target.closest("a")) {
            setIsOpen(false);
          }
        }}
      >
        {results.map((result) => {
          const item = result as ToolSearchResult;
          return (
            <ToolCard
              data={{
                description: item.description || "",
                id: item.id,
                image: item.imageUrl || "",
                slug: item.slug,
                title: item.name || "",
              }}
              key={item.id}
            />
          );
        })}
      </ul>
    );
  }

  if (activeSectionId === "9") {
    return (
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 py-6 list-none">
        {results.map((result) => {
          const item = result as Ebook;
          return <SearchEbookCard ebook={item} key={item.id} />;
        })}
      </ul>
    );
  }

  if (activeSectionId === "11") {
    return (
      <div
        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 py-6"
        onClick={(e) => {
          const target = e.target as HTMLElement;
          if (target.closest("a")) {
            setIsOpen(false);
          }
        }}
      >
        {results.map((result) => {
          const raw = result as unknown as Record<string, unknown>;
          const item: AdvisorItem = {
            actionUrl:
              (raw.actionUrl as string) || `/bilim-kurulu/${result.id}/article`,
            bio:
              (raw.bio as string) ||
              (raw.description as string) ||
              (raw.summary as string) ||
              "",
            consultingClassId: (raw.consultingClassId as string) || "",
            consultingClassName: (raw.consultingClassName as string) || "",
            id: String(result.id),
            imageUrl:
              (raw.imageUrl as string) ||
              (raw.image as string) ||
              (raw.avatar as string) ||
              (raw.coverImageUrl as string) ||
              "",
            isVerified: (raw.isVerified as boolean) ?? true,
            name: (raw.name as string) || (raw.title as string) || "",
            title: (raw.title as string) || "",
          };

          return <AdvisorCard item={item} key={result.id} />;
        })}
      </div>
    );
  }

  if (activeSectionId === "7") {
    return (
      <div className="flex flex-col gap-1 py-4">
        {results.map((result) => {
          const item = result as BabyNameSearchResult;
          return <SearchNameCard item={item} key={item.id} />;
        })}
      </div>
    );
  }

  if (activeSectionId === "5") {
    return (
      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-4 py-6"
        onClick={(e) => {
          const target = e.target as HTMLElement;
          if (target.closest("a")) {
            setIsOpen(false);
          }
        }}
      >
        {results.map((result) => {
          const item = result as AcademySearchResult;
          return <SearchAcademyCard item={item} key={item.id} />;
        })}
      </div>
    );
  }

  if (activeSectionId === "10") {
    return (
      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-4 py-6"
        onClick={(e) => {
          const target = e.target as HTMLElement;
          if (target.closest("a")) {
            setIsOpen(false);
          }
        }}
      >
        {results.map((result) => {
          const item = result as ConsultingSearchResult;
          return <SearchConsultingCard item={item} key={item.id} />;
        })}
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-6 md:gap-8 py-6"
      onClick={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest("a")) {
          setIsOpen(false);
        }
      }}
    >
      {results.map((result) => {
        if (activeSectionId === "3") {
          return (
            <div
              className="border-b border-neutral-100 pb-6 last:border-none last:pb-0"
              key={result.id}
            >
              <SearchTopicCard topic={result as Topic} />
            </div>
          );
        }

        const rawResult = result as unknown as Record<string, unknown>;
        if (rawResult.type === SECTION_TYPE_TOOLS || "toolType" in rawResult) {
          const item = result as ToolSearchResult;
          return (
            <div
              className="border-b border-neutral-100 pb-6 last:border-none last:pb-0 list-none max-w-xl"
              key={result.id}
            >
              <ToolCard
                data={{
                  description: item.description || "",
                  id: item.id,
                  image: item.imageUrl || "",
                  slug: item.slug,
                  title: item.name || "",
                }}
              />
            </div>
          );
        }

        if (rawResult.type === SECTION_TYPE_EBOOKS || "file" in rawResult) {
          const item = result as Ebook;
          return (
            <div
              className="border-b border-neutral-100 pb-6 last:border-none last:pb-0 list-none max-w-xl"
              key={result.id}
            >
              <SearchEbookCard ebook={item} />
            </div>
          );
        }

        if (
          rawResult.type === SECTION_TYPE_ACADEMY ||
          "primaryActionUrl" in rawResult
        ) {
          const item = result as AcademySearchResult;
          return (
            <div
              className="border-b border-neutral-100 pb-6 last:border-none last:pb-0"
              key={result.id}
            >
              <SearchAcademyCard item={item} />
            </div>
          );
        }

        if (rawResult.type === SECTION_TYPE_CONSULTING) {
          const item = result as ConsultingSearchResult;
          return (
            <div
              className="border-b border-neutral-100 pb-6 last:border-none last:pb-0"
              key={result.id}
            >
              <SearchConsultingCard item={item} />
            </div>
          );
        }

        if (
          rawResult.type === SECTION_TYPE_ADVISORS ||
          "consultingClassId" in rawResult
        ) {
          const item: AdvisorItem = {
            actionUrl:
              (rawResult.actionUrl as string) ||
              `/bilim-kurulu/${result.id}/article`,
            bio:
              (rawResult.bio as string) ||
              (rawResult.description as string) ||
              "",
            consultingClassId: (rawResult.consultingClassId as string) || "",
            consultingClassName:
              (rawResult.consultingClassName as string) || "",
            id: String(result.id),
            imageUrl:
              (rawResult.imageUrl as string) ||
              (rawResult.image as string) ||
              "",
            isVerified: (rawResult.isVerified as boolean) ?? true,
            name: (rawResult.name as string) || "",
            title: (rawResult.title as string) || "",
          };
          return (
            <div
              className="border-b border-neutral-100 pb-6 last:border-none last:pb-0 max-w-xs"
              key={result.id}
            >
              <AdvisorCard item={item} />
            </div>
          );
        }

        if (
          rawResult.type === SECTION_TYPE_BABY_NAMES ||
          "meaning" in rawResult
        ) {
          const item = result as BabyNameSearchResult;
          return <SearchNameCard item={item} key={result.id} />;
        }

        const contentCardProps = mapToContentCard(
          result as SearchResultContent,
        );
        return (
          <div
            className="border-b border-neutral-100 pb-6 last:border-none last:pb-0"
            key={result.id}
          >
            <SearchHorizontalContentCard content={contentCardProps} />
          </div>
        );
      })}
    </div>
  );
}
